import { FitbitMetric, FitbitPoint, FitbitRecordsPage, FitbitStoredRecord, readFitbitData } from '../../../services/fitbitCollectionDataService';
import { DateRange } from './dataAvailability';

export type PlotChoice = { metric: FitbitMetric; fieldPath: string };
export type PlotResult = PlotChoice & { points?: FitbitPoint[]; page?: FitbitRecordsPage; failed?: boolean };

// Structured charts need every record, not the first preview page. Keep the
// browser bounded to the export limits and preserve an explicit partial marker.
async function readStructuredRecords(studyId: string, participantId: string, query: Record<string, string>, signal: AbortSignal,
  reader: typeof readFitbitData): Promise<FitbitRecordsPage> {
  const records: FitbitStoredRecord[] = [];
  let revision: string | undefined;
  let offset = 0;
  let bytes = 0;
  while (true) {
    if (signal.aborted) throw new Error('Visualization cancelled');
    const page = await reader<FitbitRecordsPage>(studyId, participantId, 'records',
      { ...query, offset: String(offset), ...(revision ? { revision } : {}) });
    if (signal.aborted) throw new Error('Visualization cancelled');
    if (!/^[a-f0-9]{32}$/.test(page.revision) || (revision && revision !== page.revision)) throw new Error('Collection changed during visualization');
    if (page.nextOffset !== null && (!Number.isSafeInteger(page.nextOffset) || page.nextOffset <= offset || !page.records.length)) throw new Error('Record pagination did not advance');
    revision = page.revision;
    const kept = page.records.slice(0, 100000 - records.length);
    const pageBytes = new Blob([JSON.stringify(kept)]).size;
    if (bytes + pageBytes > 50 * 1024 * 1024) return { records, revision, nextOffset: offset };
    bytes += pageBytes;
    for (const record of kept) records.push(record);
    if (kept.length < page.records.length) return { records, revision, nextOffset: offset + kept.length };
    if (page.nextOffset === null || records.length >= 100000) return { records, revision, nextOffset: page.nextOffset };
    offset = page.nextOffset;
  }
}

// Each request still goes through the same study and participant authorization boundary.
export async function readVisualizations(studyId: string, participantId: string, range: DateRange,
  choices: PlotChoice[], signal: AbortSignal, onResult: (result: PlotResult) => void, reader: typeof readFitbitData = readFitbitData) {
  for (let offset = 0; offset < choices.length && !signal.aborted; offset += 3) {
    await Promise.all(choices.slice(offset, offset + 3).map(async choice => {
      let result: PlotResult;
      try {
        const query = { ...range, routeKey: choice.metric.routeKey, fieldPath: choice.fieldPath };
        result = choice.fieldPath
          ? { ...choice, ...await reader<{ points: FitbitPoint[] }>(studyId, participantId, 'series', query) }
          : { ...choice, page: await readStructuredRecords(studyId, participantId, query, signal, reader) };
      } catch { result = { ...choice, failed: true }; }
      if (!signal.aborted) onResult(result);
    }));
  }
}
